import {TextFieldBase as $b312f2102feb9487$export$d22444a338b6e3c2} from "./TextFieldBase.mjs";
import {useFormProps as $c29c48d4ef19ffc4$export$a6b5be5c6b451665} from "../form/Form.mjs";
import {useProviderProps as $71dfb0e0358a12de$export$521c373ccc32c300} from "../provider/Provider.mjs";
import {useTextField as $65ICI$useTextField} from "react-aria/useTextField";
import {chain as $65ICI$chain} from "react-aria/chain";
import $65ICI$react, {useRef as $65ICI$useRef, useCallback as $65ICI$useCallback, useEffect as $65ICI$useEffect} from "react";
import {useControlledState as $65ICI$useControlledState} from "react-stately/useControlledState";
import {useLayoutEffect as $65ICI$useLayoutEffect} from "react-aria/private/utils/useLayoutEffect";

/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */ 







const $14a1c0edab339433$export$f5c9f3c2c4054eec = /*#__PURE__*/ (0, $65ICI$react).forwardRef(function TextArea(props, ref) {
    props = (0, $71dfb0e0358a12de$export$521c373ccc32c300)(props);
    props = (0, $c29c48d4ef19ffc4$export$a6b5be5c6b451665)(props);
    let { isDisabled: isDisabled = false, isQuiet: isQuiet = false, isReadOnly: isReadOnly = false, isRequired: isRequired = false, onChange: onChange, ...otherProps } = props;
    // not in stately because this is so we know when to re-measure, which is a spectrum design
    let [inputValue, setInputValue] = (0, $65ICI$useControlledState)(props.value, props.defaultValue ?? '', ()=>{});
    let inputRef = (0, $65ICI$useRef)(null);
    let onHeightChange = (0, $65ICI$useCallback)(()=>{
        // Quiet textareas always grow based on their text content.
        // Standard textareas also grow by default, unless an explicit height is set.
        if ((isQuiet || !props.height) && inputRef.current) {
            let input = inputRef.current;
            let prevAlignment = input.style.alignSelf;
            let prevOverflow = input.style.overflow;
            // Firefox scroll position is lost when overflow: 'hidden' is applied so we skip applying it.
            // The measure/applied height is also incorrect/reset if we turn on and off
            // overflow: hidden in Firefox https://bugzilla.mozilla.org/show_bug.cgi?id=1787062
            let isFirefox = 'MozAppearance' in input.style;
            if (!isFirefox) input.style.overflow = 'hidden';
            input.style.alignSelf = 'start';
            input.style.height = 'auto';
            // offsetHeight - clientHeight accounts for the border/padding.
            input.style.height = `${input.scrollHeight + (input.offsetHeight - input.clientHeight)}px`;
            input.style.overflow = prevOverflow;
            input.style.alignSelf = prevAlignment;
        }
    }, [
        isQuiet,
        inputRef,
        props.height
    ]);
    (0, $65ICI$useLayoutEffect)(()=>{
        if (inputRef.current) onHeightChange();
    }, [
        onHeightChange,
        inputValue,
        inputRef
    ]);
    let hasWarned = (0, $65ICI$useRef)(false);
    (0, $65ICI$useEffect)(()=>{
        if (props.placeholder && !hasWarned.current && process.env.NODE_ENV !== 'production') {
            console.warn('Placeholders are deprecated due to accessibility issues. Please use help text instead. See the docs for details: https://react-spectrum.adobe.com/react-spectrum/TextArea.html#help-text');
            hasWarned.current = true;
        }
    }, [
        props.placeholder
    ]);
    let result = (0, $65ICI$useTextField)({
        ...props,
        onChange: (0, $65ICI$chain)(onChange, setInputValue),
        inputElementType: 'textarea'
    }, inputRef);
    return /*#__PURE__*/ (0, $65ICI$react).createElement((0, $b312f2102feb9487$export$d22444a338b6e3c2), {
        ...otherProps,
        ref: ref,
        inputRef: inputRef,
        ...result,
        multiLine: true,
        isDisabled: isDisabled,
        isQuiet: isQuiet,
        isReadOnly: isReadOnly,
        isRequired: isRequired
    });
});


export {$14a1c0edab339433$export$f5c9f3c2c4054eec as TextArea};
//# sourceMappingURL=TextArea.mjs.map
